import {FieldError, Path, useFieldArray, useFormContext} from "react-hook-form";
import RegistrationModel from "../model/RegistrationModel";
import Text from "../../../../framework/component/form/input-types/Text";
import FileUpload from "../../../../framework/component/form/input-types/FileUpload";
import {Button, Form, OverlayTrigger, Tooltip} from "react-bootstrap";
import React, {useState} from "react";
import CodeRepos from "../../../../framework/component/form/input-types/CodeRepos";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {MANDATORY_ICON} from "../../../../framework/component/form/FormComponents";

interface ProfessionalDetailsProps {
  codeRepoMandatory?: boolean
}

/**
 * References tooltip
 */
const refTooltip = (props: any) => (
  <Tooltip id="refToolTip" {...props}>
    If you do not have any PubMed references, this will not disqualify you from registering with
    UKBiobank.
  </Tooltip>
);

/**
 * CV tooltip
 */
const cvTooltip = (props: any) => (
  <Tooltip id="cvTooltip" {...props}>
    Your Résumé/CV should include details of your current institute in English.
  </Tooltip>
);

export default function ProfessionalDetail({codeRepoMandatory = false}: ProfessionalDetailsProps) {
  const {control, register, formState: {errors}, setValue} = useFormContext<RegistrationModel>();
  const {fields, append, remove} = useFieldArray({
    control,
    name: "professionalDetail.codeRepositories"
  })

  /**
   * This function is used to validate the input is numeric. To be used for the pub med refs
   * @param v The value that will be validated
   */
  const validateInputIsNumeric = (v: string): string | true => {
    // As this is optional we allow it to be empty
    if (!v || v.trim().length == 0) {
      return true;
    }

    const regex = /^[0-9 ]+$/;
    return regex.test(v)
      ? true
      : "It must be a number";
  }


  const pubMedRefs: Array<[Path<RegistrationModel>, FieldError | undefined]> = [
    ["professionalDetail.pubMed1", errors.professionalDetail?.pubMed1],
    ["professionalDetail.pubMed2", errors.professionalDetail?.pubMed2],
    ["professionalDetail.pubMed3", errors.professionalDetail?.pubMed3],
    ["professionalDetail.pubMed4", errors.professionalDetail?.pubMed4],
    ["professionalDetail.pubMed5", errors.professionalDetail?.pubMed5]
  ];

  const [repoStatus, setRepoStatus] = useState<string>("");
  const NO_REPOS_CONFIRMED = "NO_REPOS_CONFIRMED";
  const REPOS_CHECKED_AND_COMPLIANT = "REPOS_CHECKED_AND_COMPLIANT";

  return <>
    {/*TODO This needs to be modularised in the future so we can have a single selection question that
        supports panel swap*/}
    <Form.Group as={Row} className="mb-2">
      <Row>
        <Col sm={2}></Col>
        <Col sm={10} className={"question-help-text"}>
          You must inform us of any code repository usernames you have.
          Please refer to our code repository policy <a target="_blank" rel="noopener noreferrer"
                                                        href="https://community.ukbiobank.ac.uk/hc/en-gb/articles/28027475352221-Use-of-third-party-code-sharing-repositories-e-g-GitHub">here</a>.
        </Col>
      </Row>
      <Row>
        <Form.Label column sm={2}>
          Usage of Code Repositories {codeRepoMandatory && <MANDATORY_ICON/>}
        </Form.Label>
        <Col sm={10}>
          <Form.Control
            type="hidden"
            hidden
            isInvalid={!!errors.professionalDetail?.repoStatus}
            {...register("professionalDetail.repoStatus", {
              required: codeRepoMandatory ? "You must acknowledge one of the options" : false,
            })}
          />
          {/*TODO Make css class for these buttons*/}
          <Button
            type="button"
            variant={repoStatus === NO_REPOS_CONFIRMED ?
              "primary" :
              "outline-secondary"}
            style={{
              marginRight: "10px",
              marginBottom: "10px",
              width: "fit-content"
            }}

            data-testid={`no-repos-button`}
            onClick={() => {
              if (repoStatus === NO_REPOS_CONFIRMED) {
                setValue("professionalDetail.repoStatus", "", {shouldValidate: true});
                setRepoStatus("");
              } else {
                setValue("professionalDetail.repoStatus", NO_REPOS_CONFIRMED, {shouldValidate: true});
                setRepoStatus(NO_REPOS_CONFIRMED);
              }
            }}>
            I confirm that I do not have any code repositories
          </Button>
          <Button
            type="button"
            variant={repoStatus === REPOS_CHECKED_AND_COMPLIANT ?
              "primary" :
              "outline-secondary"}
            style={{
              marginRight: "10px",
              marginBottom: "10px",
              width: "fit-content"
            }}

            data-testid={`no-repos-button`}
            onClick={() => {
              if (repoStatus === REPOS_CHECKED_AND_COMPLIANT) {
                setValue("professionalDetail.repoStatus", "", {shouldValidate: true});
                setRepoStatus("");
              } else {
                setValue("professionalDetail.repoStatus", REPOS_CHECKED_AND_COMPLIANT, {shouldValidate: true});
                setRepoStatus(REPOS_CHECKED_AND_COMPLIANT);
              }
            }}>
            I have checked my code repositories are up to date and are in line with UK Biobank
            policy
          </Button>
          <Form.Control.Feedback type="invalid" data-testid="first-name-error">
            {errors.professionalDetail?.repoStatus?.message}
          </Form.Control.Feedback>
        </Col>
      </Row>
    </Form.Group>
    {repoStatus === NO_REPOS_CONFIRMED ?
      <><Form.Group as={Row} className="mb-2">
        <Row>
          <Col sm={2}></Col>
          <Col sm={10} className={"question-help-text"}>
            If at any point you start using code repositories for UK Biobank related projects you
            must notify us by adding them on your profile.
          </Col>
        </Row>
      </Form.Group></>
      : <>
        {/*The input component does not show by default, instead having a button. This ternary is to handle that change*/}
        {
          fields.length === 0 ?
            <Form.Group as={Row} className="mb-2">
              <Row>
                <Col sm={2}></Col>
                <Col sm={10} className={"question-help-text"}>
                  Please provide details of your code repository usernames
                </Col>
              </Row>
              <Row>
                <Form.Label column sm={2}>
                  Repository Usernames {codeRepoMandatory && <MANDATORY_ICON/>}
                </Form.Label>
                <Col sm={8}>
                  {fields.length < 2 &&
                      <Button variant="primary"
                              onClick={() => append({codeRepositoryId: 0, username: ""})}>
                        <i className="bi bi-plus-circle"/> Add repository
                      </Button>}
                  <Form.Control
                    type="hidden"
                    hidden
                    isInvalid={!!errors.professionalDetail?.codeRepositories}
                    {...register("professionalDetail.codeRepositories", {
                      validate: (v) => {
                        if (codeRepoMandatory) {
                          return (v.length > 0 || repoStatus === NO_REPOS_CONFIRMED) ? true : `You must add at least one code repository username`;
                        }

                        return true;
                      }
                    })}
                  />
                  <Form.Control.Feedback type="invalid" data-testid={`code-repo-error`}>
                    {errors.professionalDetail?.codeRepositories?.root?.message}
                  </Form.Control.Feedback>
                </Col>
              </Row>
            </Form.Group>
            :
            <>
              {fields.map((field, i) => (
                <CodeRepos<RegistrationModel>
                  label={i === 0 ? "Repository Usernames" : ""}
                  helpText={i === 0 ? "Please provide details of your code repository usernames" : undefined}
                  errors={errors}
                  fieldId={`professionalDetail.codeRepositories.${i}`}
                  deleteRepo={() => remove(i)}
                  mandatory={codeRepoMandatory}
                  key={`code-repo-${i}`}/>
              ))}
              <Form.Group as={Row} className="mb-2">
                <Form.Label column sm={2}>
                </Form.Label>
                <Col sm={8}>
                  <Button variant="primary"
                          onClick={() => append({codeRepositoryId: -1, username: ""})}>
                    <i className="bi bi-plus-circle"/> Add repository
                  </Button>
                </Col>
              </Form.Group>
            </>
        }</>
    }
    {/*Pub Med References*/}
    <Form.Group as={Row} className="mb-2" controlId={`formHorizontalPubMedReferences`}>
      <Row>
        <Col sm={2}></Col>
        <Col sm={10} className={"question-help-text"}>
          Please provide up to 5 PubMed references (if available). <OverlayTrigger
          placement="top"
          delay={{show: 50, hide: 200}}
          overlay={refTooltip}
        ><i className="bi bi-question-circle-fill"></i></OverlayTrigger>
        </Col>
      </Row>
      <Row>
        <Form.Label column sm={2}>
          PubMed References
        </Form.Label>
        <Col sm={10}>
          <Row>
            {pubMedRefs.map(([fieldId, error], i) => (
              <Col key={`pub-med-${i}`}>
                <Form.Control type="text" {...register(fieldId, {
                  maxLength: {
                    value: 8,
                    message: `PubMed reference is a maximum of 8 digits`
                  },
                  validate: (v) => validateInputIsNumeric(v as string)
                })}
                              isInvalid={!!error}
                              data-testid={`pubmed-${i}`}
                />
                <Form.Control.Feedback type="invalid" data-testid={`pubmed-${i}-error`}>
                  {error?.message}
                </Form.Control.Feedback>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
    </Form.Group>
    <FileUpload<RegistrationModel>
      label="CV"
      helpText={<>Please provide an up-to-date Résumé/CV in English. <OverlayTrigger
        placement="top"
        delay={{show: 50, hide: 200}}
        overlay={cvTooltip}
      ><i className="bi bi-question-circle-fill"></i></OverlayTrigger></>}
      fieldId={"professionalDetail.cvfile"}
      mandatory
      acceptedTypes={".pdf"}
    />
    <Text<RegistrationModel>
      label={"Personal Profile Link"}
      error={errors.professionalDetail?.profileLink}
      fieldId={"professionalDetail.profileLink"}/>
    <FileUpload<RegistrationModel>
      label="Complaints"
      helpText={"Please provide a copy of any complaints that have been raised against you in the last 3 years."}
      fieldId={"professionalDetail.complaintFile"}
      acceptedTypes={".pdf"}
    />
  </>
}
